import React from 'react';
import styled from 'styled-components';
import { Redirect } from 'react-router-dom';
import validator from 'validator';
import {
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Slide,
  Snackbar,
} from '@material-ui/core';
import {
  MdPersonOutline, MdLockOutline, MdSend, MdKeyboardBackspace,
} from 'react-icons/lib/md';

import Loader from '../../components/Loader';
import Auth from '../../components/Auth';
import { signInUser, sendResetPasswordLink } from '../../services/authServices';

class Login extends React.Component {
  state = {
    email: '',
    password: '',
    loading: false,
    credentialError: null,
    forgotPassword: false,
    snackbarOpened: false,
    snackbarMessage: 'Email has been sent to your id',
  };

  validateEmail = () => {
    let emailErrorMessage = null;

    if (validator.isEmpty(this.state.email)) {
      emailErrorMessage = 'Email is required';
    } else if (!validator.isEmail(this.state.email)) {
      emailErrorMessage = 'Invalid email';
    }

    this.setState({
      credentialError: emailErrorMessage,
    });

    if (!emailErrorMessage) {
      return true;
    }

    return false;
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbarOpened: false,
    });
  };

  validateAndLoginUser = async () => {
    if (this.validateEmail()) {
      this.setState({
        loading: true,
      });
      try {
        await signInUser(this.state.email, this.state.password);
        this.setState({ loading: false });
      } catch (error) {
        const { message } = error;
        this.setState({ loading: false, credentialError: message });
      }
    }
  };

  validateAndSendPasswordResetEmail = async () => {
    if (this.validateEmail()) {
      this.setState({
        loading: true,
      });
      try {
        await sendResetPasswordLink(this.state.email);
        this.setState({
          loading: false,
          snackbarOpened: true,
          email: '',
        });
      } catch (error) {
        const { message } = error;
        this.setState({ loading: false, snackbarOpened: true, snackbarMessage: message });
      }
    }
  };

  render() {
    return (
      <Auth>
        {({ authState }) => {
          if (!authState.userAuthenticated) {
            return (
              <Container>
                <LoginCard elevation={4}>
                  <CoverImage src={require('../../images/login-cover-image.jpg')}>
                    <CoverImageText variant="display1">
                      Analytics Based Assessments
                      <br />
                      for Jagran Youth Parliament
                    </CoverImageText>
                  </CoverImage>

                  <LoginInfo>
                    <Logo src={require('../../images/jagran-logo.svg')} alt="jagran-logo" />
                    <Form
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (!this.state.forgotPassword) {
                          this.validateAndLoginUser();
                        } else {
                          this.validateAndSendPasswordResetEmail();
                        }
                      }}
                    >
                      <Slide in={!this.state.forgotPassword} mountOnEnter unmountOnExit>
                        <GroupContainer>
                          <TextField
                            id="email-input"
                            placeholder="Email"
                            value={this.state.email}
                            onChange={(event) => {
                              this.setState({
                                email: event.target.value,
                                credentialError: null,
                              });
                            }}
                            helperText={this.state.credentialError}
                            error={!!this.state.credentialError}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MdPersonOutline size={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            id="password-input"
                            placeholder="Password"
                            type="password"
                            value={this.state.password}
                            onChange={(event) => {
                              this.setState({
                                password: event.target.value,
                              });
                            }}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MdLockOutline size={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button type="submit" variant="contained" color="primary" fullWidth>
                            <Spacer />
                            Login
                            <Spacer />
                            <MdSend size={24} />
                          </Button>
                          <Caption
                            variant="caption"
                            onClick={() => {
                              this.setState({ forgotPassword: true });
                            }}
                          >
                            Forgot your password?
                          </Caption>
                        </GroupContainer>
                      </Slide>

                      <Slide
                        direction="up"
                        in={this.state.forgotPassword}
                        mountOnEnter
                        unmountOnExit
                      >
                        <GroupContainer>
                          <TextField
                            id="email-input"
                            placeholder="Email"
                            value={this.state.email}
                            onChange={(event) => {
                              this.setState({
                                email: event.target.value,
                                credentialError: null,
                              });
                            }}
                            helperText={this.state.credentialError}
                            error={!!this.state.credentialError}
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MdPersonOutline size={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button type="submit" variant="contained" color="primary" fullWidth>
                            <Spacer />
                            Send password reset email
                            <Spacer />
                            <MdSend size={24} />
                          </Button>
                          <Caption
                            variant="caption"
                            onClick={() => {
                              this.setState({ forgotPassword: false });
                            }}
                          >
                            <MdKeyboardBackspace size={20} />
                            Back to login
                          </Caption>
                        </GroupContainer>
                      </Slide>
                    </Form>
                  </LoginInfo>
                </LoginCard>

                <Snackbar
                  open={this.state.snackbarOpened}
                  onClose={this.handleSnackbarClose}
                  autoHideDuration={4000}
                  message={this.state.snackbarMessage}
                />

                {this.state.loading && <Loader />}
              </Container>
            );
          }

          return (
            <Redirect
              to={{
                pathname: '/',
              }}
            />
          );
        }}
      </Auth>
    );
  }
}

export default Login;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spacer = styled.div`
  flex: 1;
`;

const LoginCard = styled(Paper)`
  width: 85%;
  height: 65%;
  display: flex;
`;

const LoginInfo = styled.div`
  position: relative;
  width: 40%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Logo = styled.img`
  height: 90px;
  object-fit: contain;
  margin-bottom: 20px;
`;

const Form = styled.form`
  position: relative;
  width: 70%;

  & > * + * {
    margin-top: 20px !important;
  }
`;

const GroupContainer = styled.div`
  & > * + * {
    margin-top: 20px !important;
  }
`;

const CoverImage = styled.div`
  position: relative;
  width: 60%;
  height: 100%;
  background-image: url(${props => props.src});
  background-position: center;
  background-size: cover;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.1));
  }
`;

const CoverImageText = styled(Typography)`
  position: absolute;
  left: 60px;
  bottom: 90px;
  color: #fff !important;
  z-index: 1;
`;

const Caption = styled(Typography)`
  color: ${props => props.theme.palette.secondary.main} !important;
  text-align: right;
  cursor: pointer;
`;



// WEBPACK FOOTER //
// ./src/pages/Login/Login.js